import { AuthId, CalendarId } from "@tento-lona/sheets";
import { Column } from "@tento-ui/component-builtin";
import { EventDetailsContent } from "@tento-ui/components/event-details/event-details-content";
import { Toast } from "@tento-ui/components/toast/toast";
import { Storybook } from "@tento-ui/ui/storybook";
import { DateTime, Duration, TimezoneRegion, Utc } from "@tento-chrono";
import { type AuthScopedCalendarDisplay } from "@tento-lona";
import { Network } from "@tento-lona/network";
import { EventFields } from "@tento-lona/sheets-ui";
import { STORYBOOK_EVENT_PRESENTATION } from "../storybook-presentation";

async function makeEventFields(
  overrides: Partial<EventFields.State> = {},
): Promise<{
  eventFields: EventFields;
  state: EventFields.State;
  viewerTz: TimezoneRegion;
}> {
  const viewerTz = await TimezoneRegion.local();
  const start = Utc.now().nearest(Duration.Time.MIN15);
  const acal: AuthScopedCalendarDisplay = [
    AuthId.of("test@example.com"),
    {
      id: CalendarId.of("primary"),
      summary: "My Calendar",
      accessRole: "owner",
      capabilities: null,
      colorId: "1",
      source: "google",
      eventsLookupKey: ":~google:example:calendar:primary:events",
    },
  ];

  const state: EventFields.State = {
    ...EventFields.draftState({
      acal,
      time: new DateTime.Range(start, start.add({ hrs: 1 })),
      isAllDay: false,
      startTz: viewerTz,
    }),
    ...overrides,
  };

  const eventFields = new EventFields();
  eventFields.bindDelegate({
    onUpdate: (_state, changedFields) =>
      Toast.info("Updated", { description: `Changed: ${[...changedFields].join(", ")}` }),
    onShowFlightInfo: () => Toast.info("Flight info"),
  });
  eventFields.bind(
    state,
    "storybook",
    viewerTz,
    [{ authId: Network.AuthId.of("test@example.com"), isPrimary: true, calendars: [acal[1]] }],
    STORYBOOK_EVENT_PRESENTATION.categories,
  );

  return { eventFields, state, viewerTz };
}

export class EventFieldsStoryBook extends Storybook.Default {
  title(): string {
    return "Event Fields";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Event Fields",
        render: async () => {
          const { eventFields } = await makeEventFields();
          const $container = EventDetailsContent.FieldContainer.make();
          $container.assignStyles({ width: "360px" });
          const fields = [
            "$calendar",
            "$description",
            "$addParticipants",
            "$tags",
            "$recurrence",
            "$videoConferencing",
            "$timezone",
            "$location",
          ];
          $container
            .bind(eventFields.$fields, fields)
            .bindVisible(new Set(fields));
          return $container;
        },
      },
      {
        title: "Long Description",
        render: async () => {
          const paragraphs: string[] = [];
          for (let i = 0; i < 20; i++) {
            paragraphs.push(
              `<p>Paragraph ${i + 1}: Lorem ipsum dolor sit amet, ` +
                `consectetur adipiscing elit. Sed do eiusmod tempor ` +
                `incididunt ut labore et dolore magna aliqua. Ut enim ad ` +
                `minim veniam, quis nostrud exercitation ullamco laboris ` +
                `nisi ut aliquip ex ea commodo consequat.</p>`,
            );
          }
          const { eventFields } = await makeEventFields({
            description: paragraphs.join(""),
          });
          const $container = EventDetailsContent.FieldContainer.make();
          $container.assignStyles({ width: "360px" });
          const fields = [
            "$calendar",
            "$description",
            "$addParticipants",
            "$tags",
            "$recurrence",
            "$videoConferencing",
            "$timezone",
            "$location",
          ];
          $container
            .bind(eventFields.$fields, fields)
            .bindVisible(new Set(fields));
          return $container;
        },
      },
      {
        title: "All Fields",
        render: async () => {
          const { eventFields } = await makeEventFields();
          const $col = Column.make();
          $col.assignStyles({ gap: "4px", padding: "16px", width: "360px" });
          for (const $field of Object.values(eventFields.$fields)) {
            $col.appendChild($field);
          }
          return $col;
        },
      },
    ];
  }
}
